import {
  ChakraButton as Button,
  ChakraGrid as Grid,
  ChakraGridItem as GridItem,
  Icons,
} from "@airhelp/react-ui-components";
import { useCallback, useState } from "react";

import type { Airport as AirportProps } from "@airhelp/react-ui-components";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useIsTouchscreen } from "hooks";
import { HERO_CHECK_COMPENSATION_EVENT, HERO_CHECK_COMPENSATION_EVENT_UA } from "modules/home-page/trackingConsts";
import type { SbEditableComponentProps } from "types/storyblok";
import { getPageUrl } from "utils/getPageUrl";
import { trackCustomEvent, trackUAEvent } from "utils/tracking";
import type { AutocompleteFormProps } from "./AutocompleteForm.d";
import autocompleteFormStyles from "./AutocompleteForm.styles";
import { AutocompleteInput } from "./AutocompleteInput";
import { FUNNEL_AB_PARAMETER } from "./consts";

export const AutocompleteForm = ({
  ctaVariant = "secondary",
  buttonText,
  variant = "wide",
  sbEditable,
}: SbEditableComponentProps<AutocompleteFormProps>) => {
  const translations = useTranslations();
  const language = useLanguage();
  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [funnelAbParameter, setFunnelAbParameter] = useState("");
  const isTouchscreen = useIsTouchscreen();
  const styles = autocompleteFormStyles(variant);

  const activeAirport: AirportProps = arrivalAirport || departureAirport;

  const getWebappPath = useCallback(() => {
    const departureAirportValue = departureAirport ? departureAirport.iata : "";
    const arrivalAirportValue = arrivalAirport ? arrivalAirport.iata : "";

    return `${getPageUrl(
      language,
      "webappNewClaim"
    )}&departureAirportIata=${departureAirportValue}&arrivalAirportIata=${arrivalAirportValue}${funnelAbParameter}`;
  }, [arrivalAirport, departureAirport, language, funnelAbParameter]);

  const onClickHandler = () => {
    if (window?.funnelAb) setFunnelAbParameter(FUNNEL_AB_PARAMETER);
    trackUAEvent(HERO_CHECK_COMPENSATION_EVENT_UA);
    trackCustomEvent(HERO_CHECK_COMPENSATION_EVENT);
  };

  return (
    <Grid templateColumns={styles.templateColumns} columnGap={2} rowGap={2} {...sbEditable}>
      <AutocompleteInput
        placeholder={translations("departure_airport")}
        setAirportSelected={setDepartureAirport}
        autoFocus={!isTouchscreen}
        instanceId="departure-input"
        dataTestId="hero-departure-input"
        key={`hero-departure-input-autofocus-${!isTouchscreen}`}
        icon={<Icons.AirplaneTakeOff boxSize={6} />}
        activeAirport={activeAirport}
      />
      <AutocompleteInput
        placeholder={translations("arrival_airport")}
        setAirportSelected={setArrivalAirport}
        instanceId="destination-input"
        dataTestId="hero-destination-input"
        activeAirport={activeAirport}
        icon={<Icons.AirplaneLanding boxSize={6} />}
      />
      <GridItem colSpan={styles.buttonColSpan}>
        <Button
          as="a"
          variant={ctaVariant}
          title={translations("check_compensation")}
          href={getWebappPath()}
          h="50px"
          mt={{ base: 4, md: 0 }}
          onClick={onClickHandler}
          data-testid="hero-cta-button"
          w="full"
        >
          {buttonText || translations("check_compensation")}
        </Button>
      </GridItem>
    </Grid>
  );
};
